import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PublicNav } from "@/components/PublicNav";
import { PublicFooter } from "@/components/PublicFooter";
import { Check } from "lucide-react";

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Pricing — VISIKT" },
      {
        name: "description",
        content:
          "Simple, honest pricing for VISIKT. Start free with up to 3 subscriptions, or upgrade to Pro for unlimited tracking and full salary history.",
      },
      { property: "og:title", content: "Pricing — VISIKT" },
      {
        property: "og:description",
        content:
          "Free and Pro plans for VISIKT. Track subscriptions, salary growth, and real purchasing power with transparent NOK pricing.",
      },
      { property: "og:url", content: "https://visikt.com/pricing" },
    ],
    links: [{ rel: "canonical", href: "https://visikt.com/pricing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Can I change plans later?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Upgrade or downgrade at any time — changes take effect immediately.",
              },
            },
            {
              "@type": "Question",
              name: "What happens to my data if I downgrade?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Your data is kept. If you have more than 3 subscriptions or 3 years of salary entries, existing entries are preserved but they will not be visible and you won't be able to add new ones until you upgrade again.",
              },
            },
            {
              "@type": "Question",
              name: "Is my financial data safe?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. All data is stored with row-level security — only you can read your data. We never sell or share it.",
              },
            },
            {
              "@type": "Question",
              name: "Do you offer refunds?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "If you're not happy within the first 14 days, email us and we'll refund you — no questions asked.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: PricingPage,
});

// Toggle between showing trial CTA or no-trial CTA.
// Set SHOW_TRIAL to true once Stripe trial is wired up.
const SHOW_TRIAL = false;

function PricingPage() {
  const { t } = useTranslation();
  const [yearly, setYearly] = useState(true);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="max-w-2xl mx-auto px-5 pt-8 flex items-center justify-between">
        <span className="font-display font-extrabold tracking-tighter text-xl">VISIKT</span>
        <PublicNav />
      </header>

      <main className="max-w-2xl mx-auto px-5 pt-16 pb-20 space-y-16">
        <section className="animate-entry">
          {SHOW_TRIAL && (
            <div className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest border border-border rounded-full px-3 py-1 mb-5 text-muted-foreground">
              {t("pricing.trialBadge")}
            </div>
          )}
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">
            {t("pricing.eyebrow")}
          </p>
          <h1 className="text-4xl font-display font-bold tracking-tight leading-[1.05] text-balance">
            {t("pricing.headline")}
          </h1>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-prose">
            {t("pricing.body")}
          </p>
        </section>

        <section className="animate-entry [animation-delay:60ms]">
          <div className="flex items-center gap-1 w-fit font-mono text-[10px] uppercase tracking-widest border border-border rounded-full overflow-hidden">
            <button
              type="button"
              onClick={() => setYearly(false)}
              className={
                "px-4 py-2 transition-colors " +
                (!yearly ? "bg-foreground text-background" : "text-muted-foreground")
              }
            >
              {t("pricing.billingToggle.monthly")}
            </button>
            <button
              type="button"
              onClick={() => setYearly(true)}
              className={
                "px-4 py-2 transition-colors flex items-center gap-2 " +
                (yearly ? "bg-foreground text-background" : "text-muted-foreground")
              }
            >
              {t("pricing.billingToggle.yearly")}
              <span
                className={
                  "text-[9px] rounded px-1.5 py-0.5 " +
                  (yearly ? "bg-background/20" : "bg-foreground/10 text-foreground")
                }
              >
                {t("pricing.pro.yearlySaving")}
              </span>
            </button>
          </div>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-entry [animation-delay:120ms]">
          <div className="border border-border rounded-2xl p-6 flex flex-col gap-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                {t("pricing.free.name")}
              </p>
              <div className="flex items-end gap-1">
                <span className="text-4xl font-display font-bold tabular-nums">
                  {t("pricing.free.price")}
                </span>
                <span className="text-sm text-muted-foreground mb-1">NOK</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{t("pricing.free.description")}</p>
            </div>

            <ul className="flex-1 space-y-2.5">
              {(t("pricing.free.features", { returnObjects: true }) as string[]).map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <Check className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/auth"
              className="block w-full py-3 text-center rounded-md border border-border text-foreground font-semibold text-sm hover:bg-foreground/5 transition-colors"
            >
              {t("pricing.free.cta")}
            </Link>
          </div>

          <div className="border-2 border-foreground rounded-2xl p-6 flex flex-col gap-6 relative">
            <div className="absolute -top-3 left-6">
              <span className="font-mono text-[9px] uppercase tracking-widest bg-foreground text-background px-2.5 py-1 rounded-full">
                {SHOW_TRIAL ? t("pricing.trialBadge") : t("pricing.pro.name")}
              </span>
            </div>

            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                {t("pricing.pro.name")}
              </p>
              <div className="flex items-end gap-1">
                <span className="text-4xl font-display font-bold tabular-nums">
                  {yearly ? t("pricing.pro.priceYearly") : t("pricing.pro.priceMonthly")}
                </span>
                <span className="text-sm text-muted-foreground mb-1">
                  NOK {yearly ? t("pricing.pro.perYear") : t("pricing.pro.perMonth")}
                </span>
              </div>
              {yearly && (
                <p className="mt-1 text-xs text-muted-foreground">
                  ≈ 57 NOK {t("pricing.pro.perMonth")}
                </p>
              )}
              <p className="mt-2 text-sm text-muted-foreground">{t("pricing.pro.description")}</p>
            </div>

            <ul className="flex-1 space-y-2.5">
              {(t("pricing.pro.features", { returnObjects: true }) as string[]).map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <Check className="size-3.5 mt-0.5 shrink-0 text-foreground" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/auth"
              className="block w-full py-3 text-center rounded-md bg-foreground text-background font-semibold text-sm hover:bg-foreground/90 transition-colors"
            >
              {SHOW_TRIAL ? t("pricing.pro.ctaWithTrial") : t("pricing.pro.ctaNoTrial")}
            </Link>
          </div>
        </section>

        <section
          id="faq"
          className="border-t border-border pt-12 animate-entry [animation-delay:180ms]"
        >
          <h2 className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-8">
            {t("pricing.faq.title")}
          </h2>
          <div className="space-y-8">
            {(["q1", "q2", "q3", "q4"] as const).map((k) => {
              const question = t(`pricing.faq.${k}`);
              const answer = t(`pricing.faq.${k.replace("q", "a")}`);
              if (!question || !answer) return null;
              return (
                <div key={k}>
                  <h3 className="font-display font-bold text-base mb-1.5">{question}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{answer}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10 pt-6 border-t border-border flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="text-muted-foreground">{t("pricing.faq.stillHave")}</span>
            <Link
              to="/contact"
              className="font-medium underline underline-offset-4 hover:text-accent transition-colors"
            >
              {t("pricing.faq.contactCta")}
            </Link>
          </div>
        </section>

        <PublicFooter />
      </main>
    </div>
  );
}
